import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Module-level constant so we don't allocate a new array on every render
// (parent re-renders would otherwise re-trigger the preloader effect).
const PRELOAD_VIDEO_URLS = Object.freeze([
  '/videos/thomas-intro.mp4',
  '/videos/mario-intro.mp4',
  '/videos/explainer-dual-system.mp4',
]);
import { useAuth } from '../auth/AuthContext';
import { useT } from '../i18n/LanguageContext';
import ExitPresentationButton from '../components/ExitPresentationButton';
import FullscreenButton from '../components/FullscreenButton';
import useDevicePower from '../hooks/useDevicePower';
import SlideDeck from '../components/SlideDeck';
import CustomCursor from '../components/CustomCursor';
import Header from '../components/Header';
import CinematicIntro from '../components/CinematicIntro';
import ContentStyles from '../components/ContentStyles';
import ThemeToggle from '../components/ThemeToggle';
import { useRemoteControl } from '../hooks/useRemoteControl';
import DiamondRainBackground from '../components/DiamondRainBackground';

import SectionHero from '../components/sections/SectionHero';
import SectionAgenda from '../components/sections/SectionAgenda';
import SectionDisclaimer from '../components/sections/SectionDisclaimer';
import SectionMarioIntro from '../components/sections/SectionMarioIntro';
import SectionThomasIntro from '../components/sections/SectionThomasIntro';
import SectionBroschure from '../components/sections/SectionBroschure';
import SectionMoeglichkeiten from '../components/sections/SectionMoeglichkeiten';
import SectionChance from '../components/sections/SectionChance';
import SectionWertentwicklung from '../components/sections/SectionWertentwicklung';
import SectionHauptgruende from '../components/sections/SectionHauptgruende';
import SectionMarkt from '../components/sections/SectionMarkt';
import SectionInnovation from '../components/sections/SectionInnovation';
import SectionGlobalNetwork from '../components/sections/SectionGlobalNetwork';
import SectionSicherheit from '../components/sections/SectionSicherheit';
import SectionBoxVideo from '../components/sections/SectionBoxVideo';
import SectionZertifiziert from '../components/sections/SectionZertifiziert';
import SectionZertifiziertVideo from '../components/sections/SectionZertifiziertVideo';
import SectionKimberley from '../components/sections/SectionKimberley';
import SectionPlaene from '../components/sections/SectionPlaene';
import SectionPlaeneVIP from '../components/sections/SectionPlaeneVIP';
import SectionVIP from '../components/sections/SectionVIP';
import SectionErsparnisse from '../components/sections/SectionErsparnisse';
import SectionMoeglichkeitenNach from '../components/sections/SectionMoeglichkeitenNach';
import SectionTraditionellerWeg from '../components/sections/SectionTraditionellerWeg';
import SectionWieMoeglich from '../components/sections/SectionWieMoeglich';
import SectionStigWeg from '../components/sections/SectionStigWeg';
import SectionDualTimeline from '../components/sections/SectionDualTimeline';
import SectionExplainer from '../components/sections/SectionExplainer';
import SectionSchliffVlog from '../components/sections/SectionSchliffVlog';
import SectionEinfacherWeg from '../components/sections/SectionEinfacherWeg';
import SectionPause from '../components/sections/SectionPause';
import SectionBusiness from '../components/sections/SectionBusiness';
import SectionUnilevelBonus from '../components/sections/SectionUnilevelBonus';
import SectionInfinityBonus from '../components/sections/SectionInfinityBonus';
import SectionMatchingBonus from '../components/sections/SectionMatchingBonus';
import SectionWorldPools from '../components/sections/SectionWorldPools';
import SectionRangBonus from '../components/sections/SectionRangBonus';
import SectionKarriere from '../components/sections/SectionKarriere';
import SectionCTA from '../components/sections/SectionCTA';
import SectionInstagram from '../components/sections/SectionInstagram';
import FreeTranslationBadge from '../components/FreeTranslationBadge';
import SectionEcosystem from '../components/sections/SectionEcosystem';
import PersistentSlideAudio from '../components/PersistentSlideAudio';
import BackgroundVideoPreloader from '../components/BackgroundVideoPreloader';

// Renders a section only when the user-configured visibility flag is truthy.
// Default (undefined) is treated as visible.
function When({ visible, children }) {
  if (visible === false) return null;
  return children;
}

export default function PresentationPage() {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const tier = useDevicePower();
  useRemoteControl();
  const vis = (user && user.section_visibility) || {};
  const agenda = (user && user.agenda) || [];

  // ESC key returns presenter to the Dashboard so they can edit the
  // Agenda, toggle slides or download the PDF hand-out without
  // reloading the app manually.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Im Vollbild beendet ESC nur das Vollbild (PPTX-Verhalten) —
        // erst der zweite ESC führt zurück zum Dashboard.
        if (document.fullscreenElement) return;
        navigate('/dashboard');
        return;
      }
      // Taste F: Vollbild umschalten (v. a. wichtig fürs Offline-Bundle,
      // das direkt im Präsentationsmodus startet und kein Dashboard hat).
      if ((e.key === 'f' || e.key === 'F') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
        e.preventDefault();
        const el = document.documentElement;
        if (!document.fullscreenElement) {
          const req = el.requestFullscreen || el.webkitRequestFullscreen;
          req?.call(el)?.catch?.(() => {});
        } else {
          document.exitFullscreen?.().catch?.(() => {});
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // Force presentation to always begin at Folie 1 (Hero) — even after Lenis
  // takes over. Disable browser scroll restoration and jump both native scroll
  // and Lenis (once initialised) back to the top.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      window.scrollTo(0, 0);
      const tries = [0, 50, 150, 350, 700, 1200];
      const timers = tries.map((t) => setTimeout(() => {
        window.scrollTo(0, 0);
        if (window.__lenis && typeof window.__lenis.scrollTo === 'function') {
          window.__lenis.scrollTo(0, { immediate: true, force: true });
        }
      }, t));
      return () => {
        window.history.scrollRestoration = prev;
        timers.forEach(clearTimeout);
      };
    }
    window.scrollTo(0, 0);
    return undefined;
  }, []);

  // Maus im Präsentationsmodus bei Inaktivität ausblenden (2.5s),
  // bei Bewegung sofort wieder anzeigen.
  useEffect(() => {
    let timer = setTimeout(() => document.body.classList.add('cursor-idle'), 2500);
    const onMove = () => {
      document.body.classList.remove('cursor-idle');
      clearTimeout(timer);
      timer = setTimeout(() => document.body.classList.add('cursor-idle'), 2500);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', onMove);
      document.body.classList.remove('cursor-idle');
    };
  }, []);

  return (
    <div className="app-shell grain slide-mode" data-testid="app-root">
      {/* Schwarzer Hintergrund (User-Wunsch): Global-Video entfernt */}
      {/* Diamantregen-Hintergrund (Nachtansicht) — Schalter in DiamondRainBackground.jsx */}
      <DiamondRainBackground />
      <ContentStyles />
      <CinematicIntro />
      <BackgroundVideoPreloader urls={PRELOAD_VIDEO_URLS} delayMs={2500} />
      <ExitPresentationButton />
      <FullscreenButton />
      {tier !== 'low' && <CustomCursor />}
      <Header />
      <FreeTranslationBadge />
      {/* Kino-Modus entfernt (User-Wunsch) */}
      <ThemeToggle />

      <SlideDeck>
          <SectionHero />
          <When visible={vis.thomasIntro ?? true}><SectionThomasIntro /></When>
          {agenda.length > 0 && <SectionAgenda items={agenda} />}
          <When visible={vis.ecosystem}><SectionEcosystem /></When>
          <SectionBroschure />
          <When visible={vis.marioIntro ?? true}><SectionMarioIntro /></When>
          <SectionDisclaimer />

          <When visible={vis.moeglichkeiten}><SectionMoeglichkeiten /></When>
          <When visible={vis.chance}><SectionChance /></When>
          <When visible={vis.wertentwicklung}><SectionWertentwicklung /></When>
          <When visible={vis.hauptgruende}><SectionHauptgruende /></When>
          <When visible={vis.markt}><SectionMarkt /></When>
          <When visible={vis.innovation}><SectionInnovation /></When>
          <When visible={vis.globalNetwork}><SectionGlobalNetwork /></When>
          <When visible={vis.zertifiziert}><SectionZertifiziert /></When>
          <When visible={vis.boxVideo ?? true}><SectionBoxVideo /></When>
          <When visible={vis.sicherheit}><SectionSicherheit /></When>
          <When visible={vis.zertifiziertVideo ?? true}><SectionZertifiziertVideo /></When>
          <When visible={vis.kimberley}><SectionKimberley /></When>
          <When visible={vis.plaene}><SectionPlaene /></When>
          <When visible={vis.vip}><SectionVIP /></When>
          <When visible={vis.plaeneVIP}><SectionPlaeneVIP /></When>
          <When visible={vis.ersparnisse}><SectionErsparnisse /></When>
          <When visible={vis.moeglichkeitenNach}><SectionMoeglichkeitenNach /></When>
          <When visible={vis.wieMoeglich ?? true}><SectionWieMoeglich /></When>
          <When visible={vis.traditionellerWeg}><SectionTraditionellerWeg /></When>
          <When visible={vis.stigWeg ?? true}><SectionStigWeg /></When>
          <When visible={vis.schliffVlog ?? true}><SectionSchliffVlog /></When>
          <When visible={vis.dualTimeline ?? true}><SectionDualTimeline /></When>
          <When visible={vis.explainer ?? true}><SectionExplainer /></When>
          <When visible={vis.einfacherWeg}><SectionEinfacherWeg /></When>
          <When visible={vis.pause}><SectionPause /></When>
          <When visible={vis.business}><SectionBusiness /></When>
          <When visible={vis.unilevelBonus}><SectionUnilevelBonus /></When>
          <When visible={vis.infinityBonus}><SectionInfinityBonus /></When>
          <When visible={vis.matchingBonus}><SectionMatchingBonus /></When>
          <When visible={vis.worldPools}><SectionWorldPools /></When>
          <When visible={vis.rangBonus}><SectionRangBonus /></When>
          <When visible={vis.karriere}><SectionKarriere /></When>
          <When visible={vis.cta}><SectionCTA /></When>
          <SectionInstagram />
      </SlideDeck>

      <PersistentSlideAudio
        src="/audio/diamond-solution.mp3"
        activeIds={['cta', 'instagram']}
        volume={0.75}
        autoStart={false}
        dataTestId="outro-audio-persistent"
      />
    </div>
  );
}
